import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { getIcon, phoneFormat } from 'utils/misc';

import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';

import Drawer from '@mui/joy/Drawer';
import Input from '@mui/joy/Input';
import ModalClose from '@mui/joy/ModalClose';
import PhoneDialpad from './PhoneDialpad';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';

import PhoneTopperCollabInfo from './PhoneTopperCollabInfo';
import usePhone from 'hooks/usePhone';
import useUserIdentity from 'hooks/useUserIdentity';
import useIncomingPhoneNumber from 'hooks/useIncomingPhoneNumber';
import useWorkKey from 'hooks/useWorkKey';
import { IncomingPhoneNumberType } from 'stores/incoming_phone_number_store';

type PhoneInputScreenType = {
    numbers: string,
    onChange: (s: string) => void,
    clearScreen: (() => void) | null
};

function PhoneInputScreen({ numbers, onChange, clearScreen }: PhoneInputScreenType) {
    const CloseIcon = getIcon('Close');
    function change(e: React.ChangeEvent<HTMLInputElement>) {
        onChange(e.target.value);
    }
    const formatted = phoneFormat(numbers);

    return (
        <Input
            size="sm"
            variant={(clearScreen ? 'outlined' : 'soft')}
            endDecorator={
                (clearScreen && numbers.length ?
                    <Button
                        variant="plain"
                        onClick={clearScreen}>
                        <CloseIcon /></Button> :
                    <Button variant="plain" disabled>
                        <CloseIcon />
                    </Button>)
            }
            value={formatted}
            onChange={change} />
    );
}


const PhoneControls = observer(({ associated_type, associated_uuid }: { associated_type: string, associated_uuid: string }) => {
    const [onCall, setOnCall] = useState(false);
    const [numberEntry, setNumberEntry] = useState('');
    const [addingParticipant, setAddingParticipant] = useState<string | null>('');
    const PhoneIcon = getIcon('LocalPhone');
    const AddCallerIcon = getIcon('AddCaller');
    const CancelIcon = getIcon('Cancel');
    const { currentIdentity } = useUserIdentity();
    const { phoneMakeCall, phoneHangUp, phoneAddParticipant, activeCallKey, sendDTMF } = usePhone();

    function startCall() {
        if (!onCall) {
            phoneMakeCall(numberEntry, associated_type, associated_uuid).then(() => {
                setOnCall(true);
            });
        }
        else if (addingParticipant) {
            if (!activeCallKey) {
                throw new Error("No active call to add participant to");
            }
            phoneAddParticipant(numberEntry, activeCallKey, currentIdentity.key).then(() => {
                setNumberEntry(addingParticipant)
                setAddingParticipant(null);
            });
        }
    }

    function endCall() {
        phoneHangUp();
        setOnCall(false);
    }

    function clearScreen() {
        setNumberEntry('');
    }



    function onPress(s: string) {
        if (s == 'Backspace') {
            if (numberEntry.length) {
                setNumberEntry(numberEntry.substring(0, numberEntry.length - 1));
            }
        }
        else if (s in "0123456789*#".split("")) {
            console.log("onPress:: %s, %o", s, onCall);
            if (onCall) {
                console.log("sending DTMF: %s", s);
                sendDTMF(s);
            }
            const n = numberEntry + s;
            setNumberEntry(n);
        }
        // setNumberEntry(s.replace(/\D/g, ''));
    }


    function onChange(s: string) {
        console.log("onChange:: %s", s);
        setNumberEntry(s.replace(/\D/g, ''));
    }

    return (
        <Stack pl={1} direction="row" spacing={1}>
            <Stack
                spacing={1}>
                <PhoneInputScreen
                    onChange={onChange}
                    clearScreen={onCall ? null : clearScreen}
                    numbers={numberEntry} />
                <PhoneDialpad onPress={onPress} />

                {(onCall && !addingParticipant) &&
                    <Button
                        fullWidth
                        variant="soft"
                        color="success"
                        startDecorator={<AddCallerIcon />}
                        onClick={() => { setAddingParticipant(numberEntry); clearScreen(); }}
                    >Add Participant</Button>}

                {(onCall && !addingParticipant) &&
                    <Button
                        fullWidth
                        variant="soft"
                        color="danger"
                        onClick={endCall}
                    >End Call</Button>}

                {addingParticipant &&
                    <Button
                        fullWidth
                        variant="plain"
                        color="neutral"
                        startDecorator={<CancelIcon />}
                        onClick={() => { setNumberEntry(addingParticipant); setAddingParticipant(null) }}
                    >Nevermind</Button>}

                {(!onCall || addingParticipant) &&
                    <Button
                        fullWidth
                        startDecorator={<PhoneIcon />}
                        disabled={(numberEntry.length < 10)}
                        color={(numberEntry.length >= 10 ? "success" : "neutral")}
                        variant="plain"
                        onClick={startCall}
                    >Dial Call</Button>
                }

            </Stack>
        </Stack>
    );

});

const PhoneTopper = observer(({ associated_type, associated_uuid }: { associated_type: string, associated_uuid: string }) => {
    const PhoneIcon = getIcon('LocalPhone');
    //    const DownArrow = getIcon('ArrowDropDown');
    const { phoneReady } = usePhone();
    const { currentIdentity } = useUserIdentity();
    const { getOrCreate } = useIncomingPhoneNumber();
    const workKey = useWorkKey();

    const [showPhoneDrawer, setPhoneDrawer] = useState(false);
    const [fetchingIncomingNumber, setFetchingIncomingNumber] = useState(false);
    const [incomingPhoneNumber, setIncomingPhoneNumber] = useState<IncomingPhoneNumberType | null>(null);

    useEffect(() => {
        if(!workKey) return;
        if(!showPhoneDrawer) return;
        console.log("getting or creating");
        setFetchingIncomingNumber(true);
        getOrCreate(workKey, associated_type, associated_uuid)
            .then((ipn) => {
                setFetchingIncomingNumber(false);
                console.log("ipn: %o", ipn);
                setIncomingPhoneNumber(ipn)
            });
    }, [currentIdentity.key, associated_type, associated_uuid, showPhoneDrawer]);


    if (!phoneReady) {
        return null;
    }

    if(currentIdentity.role != 'assistant') {
        return null;
    }


    return (
        <>
            <Stack
                px={1}
                spacing={1}>
                <Tooltip placement="left"
                    arrow title="Open the phone dialer">
                    <Button
                        size="sm"
                        variant="plain"
                        startDecorator={<PhoneIcon />}
                        onClick={() => setPhoneDrawer(!showPhoneDrawer)} >
                        Phone
                    </Button>
                </Tooltip>

            </Stack>

            {/*
            <Stack
                px={1}
                spacing={1}
                display={{ xs: 'block', sm: 'none' }}
                alignItems="center">
                <Tooltip arrow title="Open the phone dialer">
                    <Button
                        size="sm"
                        variant="outlined"
                        onClick={() => setPhoneDrawer(!showPhoneDrawer)} >
                        <PhoneIcon />
                    </Button>
                </Tooltip>

            </Stack>
              */}

            <Drawer
                anchor="top"
                size="lg"
                variant="plain"
                open={showPhoneDrawer}
                onClose={() => setPhoneDrawer(false)}

                slotProps={{
                    content: {
                        sx: {
                            bgcolor: 'transparent',
                            p: { md: 3, sm: 0 },
                            boxShadow: 'none',

                        },
                    },
                }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'md',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >

                    <ModalClose />

                    <Stack
                        pt={1}
                        spacing={1}
                    >
                        <Stack
                            alignItems="center"
                            pb={1}
                        >
                            <Typography
                                startDecorator={<PhoneIcon />}
                                level="title-lg" >
                                Phone
                            </Typography>

                            <Typography level="body-xs">
                                                            You can hide this and still use the phone.
                                         </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} >
                            <Stack>


                                <PhoneControls
                                    associated_type={associated_type}
                                    associated_uuid={associated_uuid} />


                                <Tooltip arrow
                                    title="If you need to leave a number, this is the one to use">
                                    <Stack spacing={1} mt={1}>
                                        <Divider>
                                            <Typography level="body-xs">
                                                Callback Number
                                            </Typography>
                                        </Divider>
                                            { fetchingIncomingNumber &&
                                                <Typography level="body-sm">Loading Number</Typography>
                                            }

                                            { !fetchingIncomingNumber &&
                                        <Stack
                                            mb={1}
                                            justifyContent="center"
                                            direction="row"
                                            spacing={1}>

                                            <Typography level="body-sm">
                                                {incomingPhoneNumber?.formatted_phone_number}
                                            </Typography>
                                            {incomingPhoneNumber?.extension &&
                                                <Typography level="body-sm">ext {incomingPhoneNumber?.extension}</Typography>
                                            }
                                        </Stack>
                                            }
                                    </Stack>
                                </Tooltip>
                            </Stack>
                            {/* Collab Info */}
                            <PhoneTopperCollabInfo
                                associated_type={associated_type}
                                associated_uuid={associated_uuid} />

                        </Stack>

                    </Stack>
                </Sheet>

            </Drawer>
        </>


    );


});
export default PhoneTopper;
